import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { getStripeEnvironment } from "@/lib/stripe";

interface SubscriptionState {
  subscribed: boolean;
  productId: string | null;
  priceId: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionState;
  checkSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

function hasActiveSubscriptionAccess(subscription: {
  status: string | null;
  current_period_end: string | null;
}) {
  if (subscription.status === "active" || subscription.status === "trialing") {
    return !subscription.current_period_end || new Date(subscription.current_period_end) > new Date();
  }

  return subscription.status === "canceled"
    && !!subscription.current_period_end
    && new Date(subscription.current_period_end) > new Date();
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  subscription: { subscribed: false, productId: null, priceId: null, subscriptionEnd: null, loading: true },
  checkSubscription: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionState>({
    subscribed: false,
    productId: null,
    priceId: null,
    subscriptionEnd: null,
    loading: true,
  });

  const checkSubscription = useCallback(async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession) {
      setSubscription({ subscribed: false, productId: null, priceId: null, subscriptionEnd: null, loading: false });
      return;
    }

    const userId = currentSession.user.id;
    const env = getStripeEnvironment();

    try {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("product_id, price_id, current_period_end, status")
        .eq("user_id", userId)
        .eq("environment", env)
        .order("updated_at", { ascending: false })
        .limit(5);

      if (error) throw error;

      const activeSubscription = data?.find(hasActiveSubscriptionAccess) ?? null;

      if (activeSubscription) {
        setSubscription({
          subscribed: true,
          productId: activeSubscription.product_id,
          priceId: activeSubscription.price_id,
          subscriptionEnd: activeSubscription.current_period_end,
          loading: false,
        });
      } else {
        setSubscription({ subscribed: false, productId: null, priceId: null, subscriptionEnd: null, loading: false });
      }
    } catch (err) {
      console.error("Failed to check subscription:", err);
      setSubscription(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session) {
        setTimeout(() => checkSubscription(), 0);
      } else {
        setSubscription({ subscribed: false, productId: null, priceId: null, subscriptionEnd: null, loading: false });
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session) checkSubscription();
      else setSubscription(prev => ({ ...prev, loading: false }));
    });

    return () => authSub.unsubscribe();
  }, [checkSubscription]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("subscription-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          checkSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, subscription, checkSubscription, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
